/**
 * Canonical status vocabulary — the single source of truth for the status
 * labels/tones shown across every Capture surface (Queues badge, Batches
 * header, Pending Release, Export History).
 *
 * DE-1545 F-048: pills were stale and the status wording diverged per screen.
 * This module defines ONE lifecycle vocabulary + label/tone maps so a "new
 * state" lands in one place and every surface reads the same words and colors.
 *
 * It does NOT replace the API's computed `AggregateStatus`
 * (`apps/api/src/services/batch-aggregate-status.ts`) — `aggregateStatusToLifecycle`
 * maps that onto this display vocabulary so the two stay reconciled.
 */
import { docStateLabel } from "../lifecycle/doc-state.js";
export const LIFECYCLE_STATUSES = [
    "ready",
    "in_process",
    "awaiting_release",
    "exporting",
    "exported",
    "failed",
];
export const LIFECYCLE_LABELS = {
    ready: "Ready",
    in_process: "In process",
    awaiting_release: "Awaiting release",
    exporting: "Exporting",
    exported: "Exported",
    failed: "Failed",
};
export const LIFECYCLE_TONE = {
    ready: "neutral",
    in_process: "active",
    awaiting_release: "warn",
    exporting: "active",
    exported: "success",
    failed: "error",
};
/**
 * Resolve a human label for a lifecycle state. Unknown / transitional values
 * render verbatim so a forward-compatible API value never blanks/crashes the UI.
 */
export function lifecycleLabel(status) {
    return LIFECYCLE_LABELS[status] ?? String(status);
}
export const WORKABILITY_STATUSES = [
    "available",
    "being_worked",
    "awaiting_release",
];
export const WORKABILITY_LABELS = {
    available: "Available",
    being_worked: "Being worked",
    awaiting_release: "Awaiting release",
};
export const WORKABILITY_TONE = {
    available: "neutral",
    being_worked: "active",
    awaiting_release: "warn",
};
/**
 * Resolve a human label for a workability state. Unknown / transitional values
 * render verbatim so a forward-compatible value never blanks/crashes the UI.
 */
export function workabilityLabel(status) {
    return WORKABILITY_LABELS[status] ?? String(status);
}
/**
 * DE-1545 F-060 — the per-stage BREAKDOWN line ("N available · M exporting").
 *
 * The segment words are single-sourced here so the breakdown can never drift
 * from the pill / workability wording. The "ready" bucket is rendered as
 * "available" so the word "ready" is never reused for a third concept. The
 * other buckets are stage names; `exporting` intentionally matches the
 * lifecycle word because it names the SAME concept (export in flight) — that is
 * consistent reuse, not a collision.
 */
export const BREAKDOWN_LABELS = {
    available: "available",
    awaitingClassify: "awaiting classification",
    scanning: "scanning",
    keying: "keying",
    exporting: "exporting",
    complete: "complete",
    error: "error",
};
/**
 * Resolve a human label for a breakdown bucket. Unknown values render verbatim.
 */
export function breakdownLabel(bucket) {
    return BREAKDOWN_LABELS[bucket] ?? String(bucket);
}
/**
 * Map the API's computed `AggregateStatus` (Pending / In Progress /
 * Ready for Keying / Keying in Progress / Exporting / Complete) onto the
 * canonical lifecycle. Returns `null` for an unrecognized value so the caller
 * can decide a safe fallback.
 *
 * Note: `awaiting_release` has no AggregateStatus equivalent — it is surfaced
 * via the held-batch / pending-release path, not derived from the aggregate.
 */
export function aggregateStatusToLifecycle(aggregate) {
    switch (aggregate) {
        case "Pending":
        case "Ready for Keying":
        case "Awaiting Classification":
            return "ready";
        case "In Progress":
        case "Keying in Progress":
            return "in_process";
        case "Exporting":
            return "exporting";
        case "Complete":
            return "exported";
        default:
            return null;
    }
}
/** F-074 — label for a batch-row state. Single home for the `awaiting_classify`
 *  wording so the badge and the phrase composer can never drift (everything
 *  else delegates to docStateLabel). */
export function batchRowStateLabel(state) {
    return state === "awaiting_classify" ? "Awaiting classification" : docStateLabel(state);
}
/**
 * F-074 — ONE composed status phrase per batch row, replacing the stacked
 * badge word + workability word (three colliding vocabularies). Grammar:
 * terminal states (error/complete) stand alone; active states compose
 * "<state> — <workability>".
 */
export function batchStatusPhrase(state, workability) {
    if (state === "error" || state === "complete")
        return docStateLabel(state);
    return `${batchRowStateLabel(state)} — ${workabilityLabel(workability).toLowerCase()}`;
}
//# sourceMappingURL=status-vocabulary.js.map